import {Button, Dialog, DialogActions, DialogContent, DialogTitle} from '@mui/material';
import PropTypes from 'prop-types';
import React, {useRef} from 'react';

/**
 * A dialog to display an input form dialog with standard buttons
 *
 * @module FormDialog
 *
 * @param {string} title The title of the dialog
 * @param {boolean} open A toggle to determine if the dialog is showing
 * @param {function} onClose A function to be executed when the dialog is closed
 * @param {function} render A function which returns the form component
 * @param {boolean} fullScreen Set the dialog to full screen
 * @param {string} maxWidth The maximum width for the dialog
 * @param {number} minHeight The minimum height for the dialog
 * @param {IFormDialogLabels} buttons An object providing custom labels for the save and cancel buttons
 * @param {IFormDialogButton[]} additionalButtons An array of additional buttons to show in with the default actions
 *
 * @example
 * <FormDialog
 *   title="Edit User"
 *   open={true}
 *   onClose={() => console.log('Closed')}
 *   render={(props) => <UserForm {...props} />}
 *   minHeight={400}
 *   buttons={{save: 'Save User', cancel: 'Cancel Edit'}}
 *   additionalButtons={[{label: 'Delete', colour: 'primary', onClick: () => console.log('Delete')}]}
 * />
 *
 */
const FormDialog = (
  {
    title,
    open = false,
    onClose,
    render,
    fullScreen = false,
    maxWidth = 'xl',
    minHeight,
    buttons,
    additionalButtons = []
  }
) => {
  const formRef = useRef(null);

  return (
    <Dialog open={open} onClose={() => onClose()} fullWidth={true} maxWidth={maxWidth} fullScreen={fullScreen}>
      <DialogTitle id="form-dialog-title">{title}</DialogTitle>
      <DialogContent style={minHeight ? {minHeight} : null}>
        {render({ref: formRef})}
      </DialogContent>
      <DialogActions>
        {additionalButtons.map(({label, onClick, colour}, index) => (
          <Button key={index} onClick={() => onClick()} color={colour ?? 'primary'}>
            {label}
          </Button>
        ))}
        <Button onClick={() => onClose()} variant="contained" color="error">
          {buttons?.cancel ?? 'Cancel'}
        </Button>
        <Button onClick={() => formRef.current.save()} variant="contained" color="success">
          {buttons?.save ?? 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FormDialog.propTypes = {
  title: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  render: PropTypes.func,
  fullScreen: PropTypes.bool,
  maxWidth: PropTypes.string,
  minHeight: PropTypes.number,
  buttons: PropTypes.object,
  additionalButtons: PropTypes.array
};

export default FormDialog;
